import React, { useState } from 'react';

import { InputAdornment, styled, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import Button from 'components/Button';
import Eye from 'components/SvgComponents/icons/Eye';
import EyeOff from 'components/SvgComponents/icons/EyeOff';
import LoginSubmitButton from 'containers/LoginPage/components/LoginContinueButton';
import LoginPageSubtitle from 'containers/LoginPage/components/LoginPageSubtitle';
import LoginTextField from 'containers/LoginPage/components/LoginTextField';
import LoginContainerWithBackButton from 'containers/LoginPage/containers/LoginContainerWithBackButton';
import useAuthSession from 'hooks/useAuthSession';
import useGlobalErrorHandler from 'hooks/useGlobalErrorHandler';
import globalMessages from 'translations/messages/global-messages';
import Analytics from 'utils/Analytics';

const LoginContainerWithBackButton_Password = styled(LoginContainerWithBackButton)(({ theme }) => ({
  '& .login-password-form': {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(8),
    gap: theme.spacing(4),
    '& .login-password-forgot-btn-wrapper': {
      width: '100%',
      display: 'flex',
      justifyContent: 'flex-end',
    },
  },
}));

const LoginPassword = ({ email, onBackButtonClick, onForgotPasswordClick }) => {
  const intl = useIntl();
  const history = useHistory();
  const dispatchGlobalError = useGlobalErrorHandler();
  const { loginWithPassword } = useAuthSession();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async evt => {
    setLoading(true);
    evt.preventDefault();
    try {
      await loginWithPassword(email, password);
      Analytics.track('users_sign_in_succeeded', {
        source: 'password',
      });
      history.replace('/');
    } catch (err) {
      Analytics.track('users_sign_in_failed', {
        source: 'password',
        failure_reason: err?.error || 'server_error',
      });
      dispatchGlobalError({
        error: err,
        defaultErrorMessageIntl: globalMessages.wrong_password,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainerWithBackButton_Password onBackButtonClick={onBackButtonClick}>
      <Typography variant="header" component="p">
        {intl.formatMessage(globalMessages.hello)}
      </Typography>
      <LoginPageSubtitle>{intl.formatMessage(globalMessages.enter_password)}</LoginPageSubtitle>
      <form noValidate className="login-password-form" onSubmit={handleSubmit}>
        <LoginTextField type="email" value={email} disabled />
        <div>
          <LoginTextField
            type={showPassword ? 'text' : 'password'}
            value={password || ''}
            autoFocus
            placeholder={intl.formatMessage(globalMessages.type_password)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? (
                    <Eye height={16} width={16} />
                  ) : (
                    <EyeOff height={16} width={16} />
                  )}
                </InputAdornment>
              ),
            }}
            onChange={evt => setPassword(evt.target.value)}
          />
          <div className="login-password-forgot-btn-wrapper">
            <Button variant="plain" size="small" onClick={onForgotPasswordClick}>
              {intl.formatMessage(globalMessages.forgot_password)}
            </Button>
          </div>
        </div>
        <LoginSubmitButton disabled={!password} loading={loading}>
          {intl.formatMessage(globalMessages.continue)}
        </LoginSubmitButton>
      </form>
    </LoginContainerWithBackButton_Password>
  );
};

LoginPassword.propTypes = {
  email: PropTypes.string.isRequired,
  onBackButtonClick: PropTypes.func.isRequired,
  onForgotPasswordClick: PropTypes.func.isRequired,
};

export default LoginPassword;
